import {
  AnyResolver,
  type ResolverHandle,
  ResolverMeta,
} from '../definition/resolver';
import { ResolverState } from '../definition/resolverState';
import { ResolverHttpClient } from '../definition/httpClient';
import { decorate } from '../definition/decorator';
import { Logger } from '../../devtools/logger';
import { registryDevtools } from './registryDevtools';
import { ChangeContext } from '../../client/changeManager';
import { getDefaultHttpClient } from '../../client/defaultHttpClient';
import { Metrics } from '../../metrics/metrics';
import { AnyResourceDefinition } from '../../resources/types';
import { createPersistenceDecorator } from '../../persistence/decorator';
import { PersistenceManager } from '../../persistence/persistenceManager';
import { lifecycleRules } from '../decorators/lifecycleRules';
import { changeHandling } from '../decorators/changeHandling';
import { createUnwatchManager } from '../../client/unwatchManager';

let __TEST_ONLY_DISABLE_RESOLVER_DEFER = false;

export const __testOnlyDisableResolverDefer = () => {
  __TEST_ONLY_DISABLE_RESOLVER_DEFER = true;
};

export const __testOnlyEnableResolverDefer = () => {
  __TEST_ONLY_DISABLE_RESOLVER_DEFER = false;
};

export const createResolverRegistry = ({
  logger,
  persistenceManager,
  getStoredAt,
}: {
  logger?: Logger;
  persistenceManager: PersistenceManager;
  getStoredAt: (instanceKey: string) => number | undefined;
}) => {
  const registeredResolvers = new Map<string, AnyResolver>();
  const registeredMockResolvers = new WeakMap<AnyResolver, AnyResolver>();
  const attachments = new Map<string, ResolverHandle>();
  const connectors = new Map<string, () => ResolverHandle>();
  const suspended = new Set<string>();
  const refcounts = new Map<string, number>();
  const teardown = (instanceKey: string) => {
    const attachment = attachments.get(instanceKey);

    if (!attachment) {
      return;
    }

    if (!suspended.delete(instanceKey)) {
      attachment.detach();
    }

    attachments.delete(instanceKey);
    connectors.delete(instanceKey);
  };

  const resume = (instanceKey: string) => {
    if (suspended.delete(instanceKey)) {
      logger?.debug('resuming resolver', { instanceKey });

      attachments.set(instanceKey, connectors.get(instanceKey)!());
    }
  };

  const detachManager = createUnwatchManager(teardown);

  logger?.debug('startup');

  return {
    isAttached: (instanceKey: string) => attachments.has(instanceKey),
    isReferenced: (instanceKey: string) => refcounts.has(instanceKey),
    attach: ({
      resource,
      instanceKey,
      params,
      resolver: resolverArg,
      getState,
      onResolverUpdate,
      httpClient,
    }: {
      resource: AnyResourceDefinition;
      instanceKey: string;
      params: any;
      resolver: AnyResolver;
      getState: () => ResolverState<any, any> | undefined;
      onResolverUpdate: (
        state: ResolverState<any, any>,
        meta?: ResolverMeta
      ) => void;
      httpClient?: ResolverHttpClient;
    }): boolean => {
      const attachLogger = logger?.withTags({
        instanceKey,
      });

      detachManager.clear(instanceKey);
      refcounts.set(instanceKey, (refcounts.get(instanceKey) ?? 0) + 1);

      if (attachments.has(instanceKey)) {
        return false;
      }

      const { typeName, persist } = resource;

      const applyDecorators = (target: AnyResolver) =>
        [
          lifecycleRules,
          ...(persistenceManager && persist
            ? [
                createPersistenceDecorator(
                  persistenceManager,
                  persist,
                  getStoredAt
                ),
              ]
            : []),
          changeHandling,
        ].reduce(
          (decorated, decorator) => decorated.with(decorator),
          decorate(target)
        );

      // Mock resolvers are cached by identity rather than by typeName, so a
      // type can have a mocked instance and a real instance attached at the
      // same time (mock.static seeds specific params; the rest fall through).
      let resolver: AnyResolver;
      if (
        process.env.NODE_ENV !== 'production' &&
        resolverArg !== resource.resolver
      ) {
        if (!registeredMockResolvers.has(resolverArg)) {
          attachLogger?.info('registering mock resolver');

          registeredMockResolvers.set(
            resolverArg,
            applyDecorators(resolverArg)
          );
        }

        resolver = registeredMockResolvers.get(resolverArg)!;
      } else {
        if (!registeredResolvers.has(typeName)) {
          attachLogger?.info('registering resolver');

          registeredResolvers.set(typeName, applyDecorators(resolverArg));
        }

        resolver = registeredResolvers.get(typeName)!;
      }

      attachLogger?.debug('attaching resolver');

      const defaultHttpClient = getDefaultHttpClient();

      if (httpClient && httpClient !== defaultHttpClient) {
        Metrics.counter('custom-http-client').increment();
      }

      const connect = () =>
        resolver.attach(params, {
          next: onResolverUpdate,
          getState,
          env: {
            httpClient: httpClient ?? defaultHttpClient,
            instanceKey,
            resource,
            logger: attachLogger?.withTags({ component: 'resolver' }),
          },
        });

      connectors.set(instanceKey, connect);
      attachments.set(instanceKey, connect());

      return true;
    },
    getAttachedKeys: () => attachments.keys(),
    suspend: (instanceKey: string) => {
      logger?.debug('suspending resolver', { instanceKey });

      attachments.get(instanceKey)!.detach();
      suspended.add(instanceKey);
    },
    resume,
    detach: (instanceKey: string) => {
      const detachLogger = logger?.withTags({
        instanceKey,
      });

      const remaining = (refcounts.get(instanceKey) ?? 0) - 1;

      if (remaining > 0) {
        refcounts.set(instanceKey, remaining);
        return;
      }

      refcounts.delete(instanceKey);

      detachLogger?.info('detaching resolver');

      if (__TEST_ONLY_DISABLE_RESOLVER_DEFER) {
        teardown(instanceKey);
      } else {
        detachManager.queue(instanceKey);
      }
    },
    forceDetach: (instanceKey: string) => {
      refcounts.delete(instanceKey);
      detachManager.clear(instanceKey);
      teardown(instanceKey);
    },
    evictType: (typeName: string) => {
      registeredResolvers.delete(typeName);
    },
    refresh: (instanceKey: string, changeContext?: ChangeContext) => {
      const refreshLogger = logger?.withTags({
        instanceKey,
        changeId: changeContext?.changeId,
        originId: changeContext?.originId,
      });

      const attachment = attachments.get(instanceKey);

      if (!attachment) {
        refreshLogger?.error('no attachment found');

        throw Error(
          `Attempting to refresh ${instanceKey} but no attachment found`
        );
      }

      refreshLogger?.info('refreshing resolver');

      resume(instanceKey);
      attachments.get(instanceKey)!.refresh(changeContext);
    },
    __devtools: registryDevtools(registeredResolvers),
  };
};

export type ResolverRegistry = ReturnType<typeof createResolverRegistry>;
